import { Trash2 } from "lucide-react";
import { resolveRoleSelectValue } from "../../../utils/roleSelectOptions";
import PolicyRoleSelectOptions from "./PolicyRoleSelectOptions";
import { pwInput, pwMicroLabel } from "./policyWizardFieldStyles";

const DEFAULT_APPROVAL_METHODS = [
  { value: "auto_approved", label: "Auto approved", hint: "No approval chain" },
  { value: "workflow", label: "Through workflow", hint: "Multi-step reviewers" },
];

/**
 * Onboarding-style workflow step — shared across confirmation, transfer, separation policies.
 */
export default function PolicyWorkflowStepSection({
  darkMode = false,
  policy,
  setPolicy,
  workflowStepsTab,
  setWorkflowStepsTab,
  policyRoleOptions = [],
  addWorkflowStep,
  removeWorkflowStep,
  updateWorkflowStep,
  tabs = [],
  approvalMethodOptions = DEFAULT_APPROVAL_METHODS,
  autoApprovedMessage = "Requests under this policy are approved automatically once submitted — no reviewer chain is required.",
  reminderDaysLabel = "Reminder days",
  showReminderDays = true,
  showOnInstanceOption = false,
  extendedWorkflowSettings = false,
  renderStepExtras,
}) {
  const showWorkflow =
    policy.approvalMethod === "workflow" ||
    (showOnInstanceOption && policy.approvalMethod === "on_instance" && policy.needWorkflowSteps);

  const activeTab = tabs.find((t) => t.id === workflowStepsTab) || tabs[0];
  const activeKey = activeTab?.key || "workflowSteps";
  const steps = policy[activeKey] || [];
  const chainLabel = activeTab?.chainLabel || "request";

  return (
    <div className="space-y-4">
      <div>
        <p
          className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          Approval method
        </p>
        <div
          className={`inline-flex flex-wrap p-1 rounded-xl border gap-1 ${
            darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
          }`}
        >
          {approvalMethodOptions.map((opt) => {
            const active = policy.approvalMethod === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPolicy((p) => ({ ...p, approvalMethod: opt.value }))}
                className={`px-4 py-2 rounded-lg text-left transition-all min-w-[140px] ${
                  active
                    ? "bg-[hsl(var(--ds-surface))] shadow-sm text-[hsl(var(--ds-secondary))] ring-1 ring-[hsl(var(--ds-ring))]/30"
                    : darkMode
                      ? "text-[hsl(var(--ds-muted-fg))] hover:text-white"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                }`}
              >
                <span className="block text-sm font-semibold">{opt.label}</span>
                <span className={`block text-[10px] mt-0.5 ${active ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  {opt.hint}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {showOnInstanceOption && policy.approvalMethod === "on_instance" && (
        <div
          className={`rounded-xl border px-4 py-3 ${
            darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.4)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
          }`}
        >
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!policy.needWorkflowSteps}
              onChange={(e) => setPolicy((p) => ({ ...p, needWorkflowSteps: e.target.checked }))}
              className="rounded border-[hsl(var(--ds-border))]"
            />
            <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>
              Need workflow steps to assign per instance
            </span>
          </label>
        </div>
      )}

      {policy.approvalMethod === "auto_approved" ? (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            darkMode
              ? "border-emerald-800/50 bg-emerald-950/30 text-emerald-200"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          {autoApprovedMessage}
        </div>
      ) : showWorkflow ? (
        <>
          <div
            className={`rounded-xl border p-3 ${
              darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.4)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <p
              className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Workflow settings
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {showReminderDays && policy.approvalMethod === "workflow" && (
                <div>
                  <label className={pwMicroLabel(darkMode)}>{reminderDaysLabel}</label>
                  <input
                    type="number"
                    min={0}
                    value={policy.reminderDays ?? 7}
                    onChange={(e) =>
                      setPolicy((p) => ({ ...p, reminderDays: Number(e.target.value) || 0 }))
                    }
                    className={pwInput(darkMode)}
                  />
                </div>
              )}
              <div>
                <label className={pwMicroLabel(darkMode)}>Escalation after (days)</label>
                <input
                  type="number"
                  min="1"
                  value={policy.workflowEscalationAfterDays ?? 3}
                  onChange={(e) =>
                    setPolicy((p) => ({
                      ...p,
                      workflowEscalationAfterDays: Number(e.target.value) || 3,
                    }))
                  }
                  className={pwInput(darkMode)}
                />
              </div>
              <label
                className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer ${
                  darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!policy.workflowParallelApproval}
                  onChange={(e) =>
                    setPolicy((p) => ({ ...p, workflowParallelApproval: e.target.checked }))
                  }
                  className="rounded border-[hsl(var(--ds-border))]"
                />
                <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>Allow parallel approval</span>
              </label>
              {extendedWorkflowSettings && (
                <>
                  <label
                    className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer ${
                      darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!policy.workflowAutoApprovalIfNoAction}
                      onChange={(e) =>
                        setPolicy((p) => ({ ...p, workflowAutoApprovalIfNoAction: e.target.checked }))
                      }
                      className="rounded border-[hsl(var(--ds-border))]"
                    />
                    <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>
                      Auto-approve if no action
                    </span>
                  </label>
                  <label
                    className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer ${
                      darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={policy.workflowRejectionWithReason !== false}
                      onChange={(e) =>
                        setPolicy((p) => ({ ...p, workflowRejectionWithReason: e.target.checked }))
                      }
                      className="rounded border-[hsl(var(--ds-border))]"
                    />
                    <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>
                      Rejection with reason
                    </span>
                  </label>
                  <label
                    className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer ${
                      darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={policy.workflowReinitiationAllowed !== false}
                      onChange={(e) =>
                        setPolicy((p) => ({ ...p, workflowReinitiationAllowed: e.target.checked }))
                      }
                      className="rounded border-[hsl(var(--ds-border))]"
                    />
                    <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>
                      Re-initiation allowed
                    </span>
                  </label>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                }`}
              >
                Approval chain
              </p>
              {tabs.length > 1 && (
                <div
                  className={`inline-flex flex-wrap p-0.5 rounded-lg border gap-0.5 ${
                    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                  }`}
                >
                  {tabs.map((tab) => {
                    const count = (policy[tab.key] || []).length;
                    const active = workflowStepsTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setWorkflowStepsTab(tab.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                          active
                            ? "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 shadow-sm"
                            : darkMode
                              ? "text-[hsl(var(--ds-muted-fg))] hover:text-white"
                              : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                        }`}
                      >
                        {tab.label}
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            active ? "bg-[hsl(var(--ds-surface))]/25" : darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              {steps.length === 0 ? (
                <div
                  className={`rounded-xl border border-dashed px-4 py-6 text-center ${
                    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]/30" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <p className={`text-sm font-medium ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                    No steps yet
                  </p>
                  <p className={`text-xs mt-1 mb-3 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                    Add at least one reviewer for {chainLabel} approval.
                  </p>
                  <button
                    type="button"
                    onClick={() => addWorkflowStep(activeKey)}
                    className="px-4 py-2 text-xs inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 rounded-lg hover:opacity-95"
                  >
                    + Add first step
                  </button>
                </div>
              ) : (
                steps.map((step, idx) => (
                  <div
                    key={`${activeKey}-${idx}`}
                    className={`rounded-xl border p-3 ${
                      darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]/50" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-sm"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-primary))] text-xs font-bold flex items-center justify-center border border-[hsl(var(--ds-primary))]/30">
                          {idx + 1}
                        </span>
                        <span className={`text-sm font-medium ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                          Step {idx + 1}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeWorkflowStep(activeKey, idx)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                        aria-label="Remove step"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="sm:col-span-2">
                        <label className={pwMicroLabel(darkMode)}>Approver role</label>
                        <select
                          value={resolveRoleSelectValue(policyRoleOptions, step.role)}
                          onChange={(e) =>
                            updateWorkflowStep(activeKey, idx, "role", e.target.value)
                          }
                          className={pwInput(darkMode)}
                        >
                          <PolicyRoleSelectOptions
                            roleOptions={policyRoleOptions}
                            storedValue={step.role}
                          />
                        </select>
                      </div>
                      <div>
                        <label className={pwMicroLabel(darkMode)}>SLA (days)</label>
                        <input
                          type="number"
                          min="1"
                          value={step.slaDays ?? 3}
                          onChange={(e) =>
                            updateWorkflowStep(
                              activeKey,
                              idx,
                              "slaDays",
                              Number(e.target.value) || 3
                            )
                          }
                          className={pwInput(darkMode)}
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!step.notifyOnly}
                        onChange={(e) =>
                          updateWorkflowStep(activeKey, idx, "notifyOnly", e.target.checked)
                        }
                        className="rounded border-[hsl(var(--ds-border))]"
                      />
                      <span className={darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}>
                        Notify only (no approval action required)
                      </span>
                    </label>
                    {renderStepExtras?.({
                      step,
                      idx,
                      workflowKey: activeKey,
                      darkMode,
                      updateWorkflowStep,
                    })}
                  </div>
                ))
              )}
              {steps.length > 0 && (
                <button
                  type="button"
                  onClick={() => addWorkflowStep(activeKey)}
                  className={`w-full py-2 text-xs font-medium rounded-lg border border-dashed transition-colors ${
                    darkMode
                      ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-surface))]"
                      : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  + Add another step
                </button>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
